import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import { WarrantEntity } from 'types/WarrantEntity'
import useAuth from '../../../hooks/useAuth'
import IssueApi from '../../../api/IssueApi'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import Grid from '@mui/material/Grid'
import { WarrantOriginsForm } from './AdminWarrantOriginsForm'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import { IssueEntity } from 'types/IssueEntity'
import WarrantOriginDialog from './AdminWarrantOriginDialog'
import { IssueLabel } from '../../../components/entities/IssueLabel'
import ConvertiblesApi from '../../../api/ConvertiblesApi'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { ConvertibleLabel } from '../../../components/entities/ConvertibleLabel'
import { FormOrigin } from '../types/FormWarrantEntity'

const issueApi = new IssueApi()
const convertibleApi = new ConvertiblesApi()

export function WarrantOrigins({
    warrant,
    setIsCustomFormDirty,
}: {
    warrant: WarrantEntity
    setIsCustomFormDirty: React.Dispatch<React.SetStateAction<boolean>>
}) {
    const formMethods = useFormContext()
    const [openOriginDialog, setOpenOriginDialog] = useState(false)
    const [dialogEntity, setDialogEntity] = useState<(typeof companyEntities)[number] | null>(null)
    const [companyIssues, setCompanyIssues] = useState<IssueEntity[] | null>(null)
    const [companyConvertibles, setCompanyConvertibles] = useState<ConvertibleEntity[] | null>(null)
    const [origins, setOrigins] = useState<Array<FormOrigin>>(warrant.origins || [])
    const { isAuthenticated } = useAuth()

    useEffect(() => {
        async function fetchData() {
            if (!warrant?.companyId) return
            const issues = await issueApi.findByCompanyId(warrant.companyId, false)
            const convertibles = await convertibleApi.findByCompanyId(warrant.companyId)

            setCompanyIssues(issues)
            setCompanyConvertibles(convertibles)
        }
        void fetchData()
    }, [isAuthenticated, warrant.companyId])

    const companyEntities = [...(companyIssues || []), ...(companyConvertibles || [])]

    const unlinkedEntities = companyEntities.filter(
        (entity) => !origins.some((o) => o.issueId === entity.id || o.convertibleId === entity.id),
    )

    const handleOriginDialog = (entity: (typeof companyEntities)[number] | null = null) => {
        setDialogEntity(entity)
        setOpenOriginDialog(true)
    }

    const handleAddOrigin = (origin: FormOrigin) => {
        if (!origin.id && !origin.tempId) origin.tempId = uuidv4()
        origins.push(origin)
        setIsCustomFormDirty(true)
        formMethods.setValue('origins', origins)
        setOrigins([...origins])
    }

    const handleUnlinkIssue = (originId: string) => {
        const index = origins.findIndex((i) => i.id === originId || i.tempId === originId)
        if (index >= 0) {
            origins.splice(index, 1)
            setIsCustomFormDirty(true)
            formMethods.setValue('origins', origins)
            setOrigins([...origins])
        }
    }
    return (
        <>
            <WarrantOriginDialog
                open={openOriginDialog}
                setOpen={setOpenOriginDialog}
                warrant={warrant}
                entity={dialogEntity}
                addIssueLink={handleAddOrigin}
            />
            <Typography variant='h5' gutterBottom display='block'>
                Optionernas ursprung
            </Typography>
            {origins.length === 0 && (
                <Typography variant='body1'>Inga ursprung registrerade för denna teckningsoption</Typography>
            )}
            {!companyIssues && <LinearProgress />}
            {origins.length > 0 && (
                <Grid container spacing={2}>
                    {origins.map((origin, index) => (
                        <WarrantOriginsForm
                            key={origin.id || origin.tempId}
                            namePrefix={`origins.${index}.`}
                            warrantOrigin={origin}
                            entities={companyEntities}
                            unlinkIssue={handleUnlinkIssue}
                        />
                    ))}
                </Grid>
            )}
            {companyIssues && unlinkedEntities && unlinkedEntities.length > 0 && (
                <>
                    <Divider sx={{ my: 6 }} />
                    <Grid container spacing={2}>
                        {unlinkedEntities
                            .sort(
                                (a, b) =>
                                    new Date(b.publicationDate ?? 0).getTime() -
                                    new Date(a.publicationDate ?? 0).getTime(),
                            )
                            .map((entity) => (
                                <React.Fragment key={entity.id}>
                                    <Grid size={{ xs: 12, md: 2 }}>
                                        <Button
                                            variant='outlined'
                                            color='primary'
                                            fullWidth
                                            onClick={() => handleOriginDialog(entity)}
                                        >
                                            Koppla
                                        </Button>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 10 }} key={entity.id} alignContent='center'>
                                        <Typography variant='body1'>
                                            {'type' in entity ? (
                                                <IssueLabel issue={entity} adminLink />
                                            ) : (
                                                <ConvertibleLabel convertible={entity} adminLink />
                                            )}
                                        </Typography>
                                    </Grid>
                                </React.Fragment>
                            ))}
                    </Grid>
                </>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Button variant='outlined' fullWidth onClick={() => handleOriginDialog()}>
                        Lägg till ursprung utan emission
                    </Button>
                </Grid>
            </Grid>
        </>
    )
}
